<template>
  <div class="musubi-page musubi-page--attribution">
    <header class="musubi-page__head">
      <h1>Attribution</h1>
      <p class="text-muted">
        UTM attribution + visitor-session tracking. Sessions are anonymous
        browser visits; <em>identified</em> sessions have been linked to a
        subscriber via a form-fill. <strong>First-touch</strong> is the origin
        channel — captured once and never overwritten. <strong>Last-touch</strong>
        is the channel of the most recent converting session — overwritten on
        every form-fill. Compare them to see what brings leads in vs. what closes
        them.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <section class="actions-bar">
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- KPI cards -->
    <section class="attr-kpis">
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.sessions.total) }}</div>
        <div class="attr-kpi__label">Visitor sessions</div>
      </div>
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.sessions.identified) }}</div>
        <div class="attr-kpi__label">Identified sessions</div>
        <div class="attr-kpi__sub">{{ pct(summary.sessions.identified, summary.sessions.total) }} of sessions</div>
      </div>
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.subscribers.attributed) }}</div>
        <div class="attr-kpi__label">First-touch attributed</div>
        <div class="attr-kpi__sub">{{ pct(summary.subscribers.attributed, summary.subscribers.total) }} of {{ fmt(summary.subscribers.total) }}</div>
      </div>
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.subscribers.last_attributed) }}</div>
        <div class="attr-kpi__label">Last-touch attributed</div>
        <div class="attr-kpi__sub">{{ pct(summary.subscribers.last_attributed, summary.subscribers.total) }} of {{ fmt(summary.subscribers.total) }}</div>
      </div>
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.events.pageview) }}</div>
        <div class="attr-kpi__label">Page views</div>
      </div>
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.events.email_open) }}</div>
        <div class="attr-kpi__label">Email opens</div>
      </div>
      <div class="attr-kpi">
        <div class="attr-kpi__value">{{ fmt(summary.events.email_click) }}</div>
        <div class="attr-kpi__label">Email clicks</div>
      </div>
    </section>

    <h5 class="attr-section__title">First-touch <span class="text-muted">— origin channel (sessions → conversions)</span></h5>
    <div class="row g-4">
      <div class="col-lg-6">
        <h6 class="attr-breakdown__title">By UTM source</h6>
        <MusubiTabulator
          :rows="summary.by_source"
          :columns="breakdownColumns"
          storage-key="admin.attribution.source"
          :initial-sort="{ column: 'sessions', dir: 'desc' }"
          :page-size="10"
          :loading="busy.load"
          :error="error"
        />
      </div>
      <div class="col-lg-6">
        <h6 class="attr-breakdown__title">By UTM campaign</h6>
        <MusubiTabulator
          :rows="summary.by_campaign"
          :columns="breakdownColumns"
          storage-key="admin.attribution.campaign"
          :initial-sort="{ column: 'sessions', dir: 'desc' }"
          :page-size="10"
          :loading="busy.load"
          :error="error"
        />
      </div>
    </div>

    <h5 class="attr-section__title attr-section__title--last">Last-touch <span class="text-muted">— closing channel (attributed subscribers)</span></h5>
    <div class="row g-4">
      <div class="col-lg-6">
        <h6 class="attr-breakdown__title">By UTM source</h6>
        <MusubiTabulator
          :rows="summary.last_touch.by_source"
          :columns="lastTouchColumns"
          storage-key="admin.attribution.last_source"
          :initial-sort="{ column: 'subscribers', dir: 'desc' }"
          :page-size="10"
          :loading="busy.load"
          :error="error"
        />
      </div>
      <div class="col-lg-6">
        <h6 class="attr-breakdown__title">By UTM campaign</h6>
        <MusubiTabulator
          :rows="summary.last_touch.by_campaign"
          :columns="lastTouchColumns"
          storage-key="admin.attribution.last_campaign"
          :initial-sort="{ column: 'subscribers', dir: 'desc' }"
          :page-size="10"
          :loading="busy.load"
          :error="error"
        />
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";

const EMPTY_SUMMARY = {
  sessions:    { total: 0, identified: 0 },
  subscribers: { total: 0, attributed: 0, last_attributed: 0 },
  events:      { pageview: 0, email_open: 0, email_click: 0 },
  by_source:   [],
  by_campaign: [],
  last_touch:  { by_source: [], by_campaign: [] },
};

export default {
  name: 'AdminAttribution',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      summary: structuredClone(EMPTY_SUMMARY),
      busy: { load: false },
      error: null,
    };
  },
  computed: {
    breakdownColumns() {
      return [
        { key: 'value',       label: 'Source / campaign' },
        { key: 'sessions',    label: 'Sessions',    width: 110, sorter: 'number' },
        { key: 'conversions', label: 'Conversions', width: 120, sorter: 'number' },
        { key: 'conv_rate',   label: 'Conv. rate',  width: 110, sorter: 'number',
          format: (v) => (v == null ? '—' : `${v.toFixed(1)}%`) },
      ];
    },
    lastTouchColumns() {
      return [
        { key: 'value',       label: 'Source / campaign' },
        { key: 'subscribers', label: 'Subscribers', width: 130, sorter: 'number' },
      ];
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      this.busy.load = true;
      this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/attribution/summary');
        const payload = res?.data ?? res;
        // Merge over the empty shape so a partial payload never breaks render.
        this.summary = {
          sessions:    { ...EMPTY_SUMMARY.sessions,    ...(payload?.sessions || {}) },
          subscribers: { ...EMPTY_SUMMARY.subscribers, ...(payload?.subscribers || {}) },
          events:      { ...EMPTY_SUMMARY.events,      ...(payload?.events || {}) },
          by_source:   this.decorate(payload?.by_source),
          by_campaign: this.decorate(payload?.by_campaign),
          last_touch: {
            by_source:   Array.isArray(payload?.last_touch?.by_source) ? payload.last_touch.by_source : [],
            by_campaign: Array.isArray(payload?.last_touch?.by_campaign) ? payload.last_touch.by_campaign : [],
          },
        };
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load attribution data.';
      }
      this.busy.load = false;
    },
    /** Add a numeric conv_rate (%) to each breakdown row so the column sorts. */
    decorate(rows) {
      if (!Array.isArray(rows)) return [];
      return rows.map((r) => {
        const sessions = Number(r.sessions || 0);
        const conversions = Number(r.conversions || 0);
        return {
          ...r,
          conv_rate: sessions > 0 ? (conversions / sessions) * 100 : null,
        };
      });
    },
    fmt(n) {
      return Number(n || 0).toLocaleString();
    },
    pct(part, whole) {
      const p = Number(part || 0);
      const w = Number(whole || 0);
      if (w <= 0) return '—';
      return `${((p / w) * 100).toFixed(1)}%`;
    },
  },
};
</script>

<style>
.musubi-page--attribution { padding: var(--m-space-6, 32px); }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }

.attr-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.attr-kpi {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 14px);
  box-shadow: var(--m-shadow-card);
  padding: 16px 18px;
}
.attr-kpi__value {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--m-text-primary, #0F172A);
}
.attr-kpi__label {
  margin-top: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--m-text-secondary, #475569);
}
.attr-kpi__sub {
  margin-top: 2px;
  font-size: 0.72rem;
  color: var(--m-text-tertiary, #94A3B8);
}
.attr-section__title {
  margin: 8px 0 14px;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--m-text-primary, #0F172A);
}
.attr-section__title--last {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--m-border, #E2E8F0);
}
.attr-breakdown__title {
  margin-bottom: 10px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--m-text-tertiary, #94A3B8);
}
</style>
